import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ErrorType } from "../../types/error.definition";
import { RootState } from "../store";

// Action type string used instead of importing sessionSlice to avoid circular deps
const VALIDATE_SESSION_FAILURE = "session/validateSessionFailure";

export interface ApiErrorField {
  name: string;
  message: string;
}

export interface ApiError {
  code: string;
  message?: string;
  fields?: ApiErrorField[];
  category?: ErrorType | string;
}

interface ErrorState {
  error: ApiError | null;
  isModalOpen: boolean;
  isGenericError: boolean;
}

const initialState: ErrorState = {
  error: null,
  isModalOpen: false,
  isGenericError: false,
};

const errorSlice = createSlice({
  name: "error",
  initialState,
  reducers: {
    setApiError: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.isModalOpen = true;
      state.isGenericError = false;
    },
    setGenericError: (state, action: PayloadAction<string>) => {
      state.error = {
        code: "GENERIC_ERROR",
        message: action.payload,
      };
      state.isModalOpen = true;
      state.isGenericError = true;
    },
    clearError: state => {
      state.error = null;
      state.isModalOpen = false;
      state.isGenericError = false;
    },
  },
  extraReducers: builder => {
    // When session validation fails the session is simply expired/invalid — never
    // show an error modal for this. Both reducers run in the same Redux dispatch
    // cycle so SessionProvider renders children with isModalOpen:false from the start.
    builder.addCase(VALIDATE_SESSION_FAILURE, state => {
      state.error = null;
      state.isModalOpen = false;
      state.isGenericError = false;
    });
  },
});

export const { setApiError, setGenericError, clearError } = errorSlice.actions;

export default errorSlice.reducer;

export const selectError = (state: RootState) => state.error;
